import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Upload, Sparkles, Camera, Loader2, CheckCircle2, Shield, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Sample brief shown BEFORE upload — so prospects understand exactly
 * what they'll receive. Removes all "what does this even do?" friction.
 */
const SAMPLE_BRIEF = {
  summary:
    "Two-story brick retail storefront with moderate biological growth on the north facade, efflorescence around window sills, and heavy gum accumulation on the sidewalk near the entrance. Overall property presents dated but is fully recoverable — no structural concerns visible.",
  visible_issues: [
    "Algae / mildew band across upper brick course (north-facing)",
    "Efflorescence (white salt bloom) on limestone sills",
    "Gum + oil staining on 40-ft sidewalk apron",
    "Awning fabric shows UV fade + minor bird soiling",
  ],
  recommended_services: [
    { priority: "NOW", service: "Soft-wash facade restoration", why: "Removes bio-growth without damaging masonry. Highest curb-appeal lift." },
    { priority: "30d", service: "Sidewalk hot-water surface clean", why: "Restores concrete tone. Reduces slip liability from gum." },
    { priority: "90d", service: "Awning brightening + protectant", why: "Extends fabric life by ~3 years. Prevents replacement." },
  ],
  recommended_cadence: "Monthly",
  estimated_tier: "Guardian ($1,499/mo)",
  next_step: "This is a 4-hour service window with a two-tech crew. We can walk the site tomorrow.",
};

export default function Audit() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (!file) { setPreview(""); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function onPick(e) {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  }

  async function submit() {
    if (!file) { toast.error("Upload a property photo first."); return; }
    setLoading(true); setBrief(null);
    const fd = new FormData();
    fd.append("image", file);
    fd.append("property_type", propertyType);
    fd.append("location", location);
    fd.append("notes", notes);
    try {
      const res = await axios.post(`${API}/audit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBrief(res.data.brief);
      // Scroll the brief into view on mobile
      setTimeout(() => document.getElementById("audit-brief-panel")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    } catch (e) {
      toast.error("Audit failed — please try again.");
    } finally { setLoading(false); }
  }

  return (
    <div data-testid="audit-page" style={{ background: "var(--bg)", color: "var(--text)", minHeight: "100vh" }}>
      {/* HERO */}
      <section className="pt-24 md:pt-32 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(900px 500px at 20% 30%, rgba(198,168,124,0.10), transparent 60%)"
        }} />
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14 relative">
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <div className="overline">The AI Property Audit</div>
              <h1 className="font-display mt-5" style={{ fontSize: "clamp(2.8rem, 7vw, 6rem)", lineHeight: 0.95, letterSpacing: "-0.04em" }}>
                Upload one photo. <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>Get a real maintenance report — in sixty seconds.</span>
              </h1>
              <p className="mt-8 font-body text-lg md:text-xl max-w-2xl" style={{ color: "var(--text-2)", lineHeight: 1.7 }}>
                Snap your storefront, facade, parking lot, or entryway. Our AI vision — trained on eleven years of Bespoke field records — reads the frame and hands you a written scope of work with visible issues, recommended services, and a rough tier.
              </p>
              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>
                <div className="flex items-center gap-2"><Zap size={12} style={{ color: "var(--gold)" }} /> Under 60 seconds</div>
                <div className="flex items-center gap-2"><Shield size={12} style={{ color: "var(--gold)" }} /> No signup · No spam</div>
                <div className="flex items-center gap-2"><CheckCircle2 size={12} style={{ color: "var(--gold)" }} /> 100% free</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section className="pb-24">
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14 grid md:grid-cols-12 gap-8">
          {/* Left — Uploader form */}
          <div className="md:col-span-5">
            <div className="glass-card p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: "var(--gold)", color: "#0a0a0d" }}>
                  <span className="font-mono text-[11px] font-bold">1</span>
                </div>
                <div className="font-display text-2xl" style={{ letterSpacing: "-0.02em" }}>Upload a photo</div>
              </div>
              <p className="font-body text-sm mb-5" style={{ color: "var(--text-2)", lineHeight: 1.65 }}>
                One clear shot of your property — phone camera is fine. Storefront, facade, sidewalk, dumpster area, or parking lot.
              </p>

              <label
                htmlFor="audit-file"
                className="block cursor-pointer relative aspect-[4/3] w-full overflow-hidden transition-all"
                style={{
                  background: "rgba(255,255,255,0.02)",
                  border: preview ? "1px solid var(--line-gold)" : "1.5px dashed rgba(198,168,124,0.35)",
                  borderRadius: "18px",
                }}
                data-cursor="hover"
                data-cursor-label="Upload"
                data-testid="audit-dropzone"
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="w-full h-full object-cover" style={{ borderRadius: "16px" }} />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
                    <Camera size={30} style={{ color: "var(--gold)" }} strokeWidth={1.4} />
                    <div className="font-body text-base" style={{ color: "var(--text)" }}>Drop photo here or click to browse</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--text-3)" }}>JPG · PNG · WEBP · &lt; 8MB</div>
                  </div>
                )}
              </label>
              <input id="audit-file" ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid="audit-file-input" />

              <div className="mt-6 space-y-4">
                <div>
                  <label className="overline block mb-2">Property Type (optional)</label>
                  <input
                    data-testid="audit-property-type"
                    value={propertyType}
                    onChange={(e) => setPropertyType(e.target.value)}
                    placeholder="e.g. retail storefront, medical, parking lot"
                    className="w-full px-4 py-3 font-body text-sm"
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: "12px",
                      color: "var(--text)",
                    }}
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Location (optional)</label>
                  <input
                    data-testid="audit-location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g. Charlotte NC · Brooklyn NY"
                    className="w-full px-4 py-3 font-body text-sm"
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: "12px",
                      color: "var(--text)",
                    }}
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Anything we should know? (optional)</label>
                  <textarea
                    data-testid="audit-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. water restrictions on-site, weekend-only access…"
                    className="w-full px-4 py-3 font-body text-sm min-h-[80px]"
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: "12px",
                      color: "var(--text)",
                    }}
                  />
                </div>
              </div>

              <button
                onClick={submit}
                disabled={loading || !file}
                data-testid="audit-submit"
                data-cursor="hover"
                data-cursor-label="Analyze"
                className="btn-glow w-full justify-center mt-6"
                style={{ opacity: (!file || loading) ? 0.55 : 1 }}
              >
                {loading ? <><Loader2 size={14} className="animate-spin" /> Analyzing your photo…</> : <><Sparkles size={14} /> Generate My Report</>}
              </button>

              <div className="mt-4 font-mono text-[9px] uppercase tracking-[0.28em] text-center" style={{ color: "var(--text-3)" }}>
                No signup required · No credit card · Free forever
              </div>
            </div>
          </div>

          {/* Right — Brief output */}
          <div className="md:col-span-7" id="audit-brief-panel">
            <div className="glass-card p-6 md:p-10 min-h-[560px] relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: "var(--gold)", color: "#0a0a0d" }}>
                  <span className="font-mono text-[11px] font-bold">2</span>
                </div>
                <div className="font-display text-2xl" style={{ letterSpacing: "-0.02em" }}>Your maintenance report</div>
              </div>

              {loading && (
                <div className="mt-24 text-center">
                  <Loader2 size={40} className="mx-auto animate-spin" style={{ color: "var(--gold)" }} />
                  <div className="font-display text-2xl mt-6" style={{ letterSpacing: "-0.02em" }}>Reading your photo…</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.28em] mt-3" style={{ color: "var(--text-3)" }}>
                    Identifying issues · Matching services · Pricing tier
                  </div>
                </div>
              )}

              {!brief && !loading && (
                <div>
                  <div className="mb-6 px-4 py-3 flex items-center gap-3" style={{
                    background: "rgba(198,168,124,0.08)",
                    border: "1px solid rgba(198,168,124,0.25)",
                    borderRadius: "12px",
                  }}>
                    <Sparkles size={14} style={{ color: "var(--gold)" }} />
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--gold-2)" }}>
                      Preview · this is what you'll receive
                    </div>
                  </div>
                  <BriefRender brief={SAMPLE_BRIEF} isSample />
                </div>
              )}

              {brief && !loading && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="audit-brief">
                  <div className="mb-6 px-4 py-3 flex items-center gap-3" style={{
                    background: "rgba(74,222,128,0.08)",
                    border: "1px solid rgba(74,222,128,0.25)",
                    borderRadius: "12px",
                  }}>
                    <CheckCircle2 size={14} style={{ color: "#4ade80" }} />
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "#4ade80" }}>
                      Report ready
                    </div>
                  </div>
                  <BriefRender brief={brief} />
                </motion.div>
              )}
            </div>

            {/* Why this exists — trust block */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { Icon: Zap, title: "Instant", body: "Report generated in ~30 seconds. No email chase." },
                { Icon: Shield, title: "Private", body: "Your photo is analyzed and discarded. Never sold." },
                { Icon: CheckCircle2, title: "Actionable", body: "Real services with priority, not sales pitches." },
              ].map((t, i) => (
                <div key={i} className="p-5" style={{
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: "14px",
                }}>
                  <t.Icon size={16} style={{ color: "var(--gold)" }} />
                  <div className="mt-3 font-display text-lg" style={{ letterSpacing: "-0.02em" }}>{t.title}</div>
                  <div className="mt-1 font-body text-sm" style={{ color: "var(--text-2)", lineHeight: 1.55 }}>{t.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* Shared brief rendering — used for both sample preview and real result */
function BriefRender({ brief, isSample = false }) {
  return (
    <div>
      <div className="overline mb-3">Summary</div>
      <p className="font-body text-base md:text-lg" style={{ color: "var(--text)", lineHeight: 1.6 }}>
        {brief.summary}
      </p>

      {brief.visible_issues?.length > 0 && (
        <div className="mt-8">
          <div className="overline mb-3">Visible issues</div>
          <ul className="space-y-2.5">
            {brief.visible_issues.map((v, i) => (
              <li key={i} className="pl-6 relative font-body text-[15px]" style={{ color: "var(--text-2)", lineHeight: 1.55 }}>
                <span className="absolute left-0 top-1" style={{ color: "var(--gold)" }}>▸</span>{v}
              </li>
            ))}
          </ul>
        </div>
      )}

      {brief.recommended_services?.length > 0 && (
        <div className="mt-8">
          <div className="overline mb-3">Recommended services</div>
          <div className="space-y-3">
            {brief.recommended_services.map((r, i) => (
              <div key={i} className="p-4 flex items-start gap-4" style={{
                background: "rgba(198,168,124,0.05)",
                border: "1px solid rgba(198,168,124,0.18)",
                borderRadius: "14px",
              }}>
                <span className="font-mono text-[9px] uppercase tracking-[0.24em] px-2.5 py-1 shrink-0" style={{
                  background: "var(--gold)",
                  color: "#0a0a0d",
                  borderRadius: "999px",
                }}>{r.priority}</span>
                <div>
                  <div className="font-display text-lg" style={{ letterSpacing: "-0.02em" }}>{r.service}</div>
                  <div className="text-sm mt-1" style={{ color: "var(--text-2)", lineHeight: 1.5 }}>{r.why}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="p-4" style={{
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "14px",
        }}>
          <div className="overline">Suggested cadence</div>
          <div className="font-display text-2xl mt-1">{brief.recommended_cadence}</div>
        </div>
        <div className="p-4" style={{
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "14px",
        }}>
          <div className="overline">Suggested tier</div>
          <div className="font-display text-2xl mt-1">{brief.estimated_tier}</div>
        </div>
      </div>

      <div className="mt-6 p-5" style={{
        background: "linear-gradient(135deg, rgba(198,168,124,0.14), rgba(198,168,124,0.06))",
        border: "1px solid rgba(198,168,124,0.35)",
        borderRadius: "16px",
      }}>
        <div className="overline" style={{ color: "var(--gold-2)" }}>Next step</div>
        <p className="mt-2 font-body" style={{ color: "var(--text)", lineHeight: 1.55 }}>{brief.next_step}</p>
        {!isSample && (
          <Link
            to="/quote"
            data-cursor="hover"
            data-cursor-label="Book"
            className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.28em]"
            style={{ background: "var(--gold)", color: "#0a0a0d", borderRadius: "999px" }}
            data-testid="audit-turn-into-quote"
          >
            Turn this into a formal quote <ArrowUpRight size={14} />
          </Link>
        )}
        {isSample && (
          <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--text-3)" }}>
            ↖ Upload your photo on the left to get your own report
          </div>
        )}
      </div>
    </div>
  );
}
